/**
 * Customer TanStack Query Hooks (T078, T145-T146)
 * Data fetching and mutations for customer management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

// Types

export interface Customer {
  id: string;
  userId: string;
  email: string;
  name: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dateOfBirth?: string;
  notes?: string;
  tags: string[];
  taxExempt: boolean;
  archived: boolean;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
  appointmentsCount: number;
  invoicesCount: number;
  paymentsCount?: number;
}

export interface CustomerProfile extends Customer {
  healthScore: {
    score: number;
    breakdown: {
      recency: number;
      frequency: number;
      payment: number;
      engagement: number;
    };
  };
  recentAppointments: Array<{
    id: string;
    title: string;
    startTime: string;
    endTime: string;
    status: string;
  }>;
  recentInvoices: Array<{
    id: string;
    invoiceNumber: string;
    total: string;
    status: string;
    createdAt: string;
  }>;
  counts: {
    appointments: number;
    invoices: number;
    payments: number;
  };
}

export interface CustomerAppointment {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: string;
  location?: string;
  notes?: string;
  createdAt: string;
}

export interface CustomerInvoice {
  id: string;
  invoiceNumber: string;
  total: string;
  subtotal?: string;
  tax?: string;
  status: string;
  dueDate?: string;
  paidAt?: string;
  createdAt: string;
}

export interface CustomerPayment {
  id: string;
  amount: string;
  method: string;
  status: string;
  referenceNumber?: string;
  invoiceNumber?: string;
  createdAt: string;
}

export interface CommunicationLog {
  id: string;
  type: string;
  direction: string;
  subject?: string;
  body?: string;
  createdAt: string;
  createdBy?: string;
}

interface PaginatedList<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Query Hooks

export function useCustomer(customerId: string) {
  return useQuery({
    queryKey: ['customer', customerId],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<CustomerProfile>>(
        `/customers/${customerId}`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

export function useCustomerAppointments(customerId: string, page = 1, limit = 10) {
  return useQuery({
    queryKey: ['customer', customerId, 'appointments', page],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PaginatedList<CustomerAppointment>>>(
        `/customers/${customerId}/appointments?page=${page}&limit=${limit}`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

export function useCustomerInvoices(customerId: string, page = 1, limit = 10) {
  return useQuery({
    queryKey: ['customer', customerId, 'invoices', page],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PaginatedList<CustomerInvoice>>>(
        `/customers/${customerId}/invoices?page=${page}&limit=${limit}`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

export function useCustomerPayments(customerId: string, page = 1, limit = 10) {
  return useQuery({
    queryKey: ['customer', customerId, 'payments', page],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PaginatedList<CustomerPayment>>>(
        `/customers/${customerId}/payments?page=${page}&limit=${limit}`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

export function useCustomerCommunications(customerId: string, page = 1, limit = 20) {
  return useQuery({
    queryKey: ['customer', customerId, 'communications', page],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PaginatedList<CommunicationLog>>>(
        `/customers/${customerId}/communication-logs?page=${page}&limit=${limit}`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

// Mutation Hooks

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Customer> }) => {
      const response = await apiClient.put<ApiResponse<Customer>>(`/customers/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomerTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, tags }: { id: string; tags: string[] }) => {
      const response = await apiClient.put<ApiResponse<Customer>>(`/customers/${id}/tags`, {
        tags,
      });
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useArchiveCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<Customer>>(`/customers/${id}/archive`);
      return response.data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useRestoreCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<Customer>>(`/customers/${id}/restore`);
      return response.data.data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['customer', id] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useCreateCommunication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      data,
    }: {
      customerId: string;
      data: { type: string; direction: string; subject?: string; body?: string };
    }) => {
      const response = await apiClient.post<ApiResponse<CommunicationLog>>(
        `/customers/${customerId}/communication-logs`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['customer', variables.customerId, 'communications'],
      });
    },
  });
}

// ============================================================================
// PORTAL STATUS HOOKS (T145-T146)
// ============================================================================

export interface PortalUserStatus {
  hasPortalAccess: boolean;
  status: 'NOT_INVITED' | 'INVITATION_PENDING' | 'ACTIVE' | 'DEACTIVATED';
  portalUser?: {
    id: string;
    email: string;
    invitationSentAt: string | null;
    invitationAcceptedAt: string | null;
    lastLoginAt: string | null;
    isActive: boolean;
  };
}

export function usePortalStatus(customerId: string) {
  return useQuery({
    queryKey: ['customer', customerId, 'portal-status'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PortalUserStatus>>(
        `/customers/${customerId}/portal-status`
      );
      return response.data.data;
    },
    enabled: !!customerId,
  });
}

export function useSendPortalInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customerId: string) => {
      const response = await apiClient.post<ApiResponse<{ portalUserId: string; email: string }>>(
        `/customers/${customerId}/portal-invitation`
      );
      return response.data.data;
    },
    onSuccess: (_data, customerId) => {
      queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'portal-status'] });
    },
  });
}
